import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from "vue";
import { EnumWorkClassification } from "@/models/enum/Enum";
import { InputRules } from "@/rules";
import { VacationFlowDat } from "@/models/transaction/VacationFlowDat";
import {
  PulldownResponse,
  codeKbnFilter
} from "@/models/response/PulldownResponse";
import { CodeKbnList, CodeKbnType } from "@/models/codeMaster/Code";
import { $get, $post } from "@/plugins/api/api";
import { Paths } from "@/plugins/api/paths";
import { $alert } from "@/plugins/alert/alert";
import { DateTime } from "luxon";

interface VForm extends Vue {
  reset(): void;
  resetValidation(): void;
  validate(): boolean;
}

type ReasonKbn = typeof ReasonKbn[keyof typeof ReasonKbn];

type Props = {
  item: VacationFlowDat;
  disabled: boolean;
};
type Emits = {
  (e: "close"): void;
};
type Item = {
  text: string;
  value: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'VacationDialog',
  props: {
    item: null,
    disabled: { type: Boolean }
  },
  emits: ["close"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: "close"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  item: VacationFlowDat;
  disabled: boolean;
};

const vacationKbn = [
  { text: "有給", value: EnumWorkClassification.PAID_HOLIDAY },
  { text: "欠勤", value: EnumWorkClassification.ABSENCE_DAY },
  { text: "特別休暇", value: EnumWorkClassification.SPECIAL_HOLIDAY },
  { text: "その他", value: EnumWorkClassification.OTHER }
];

const ReasonKbn = {
  self: "1",
  ceremonial: "2",
  special: "3"
} as const;
const reasonKbn = [
  { text: "私用", value: ReasonKbn.self },
  { text: "慶弔", value: ReasonKbn.ceremonial },
  { text: "特別", value: ReasonKbn.special }
];




const vform = ref<VForm>();

const dat = ref(Object.assign({}, props.item));

const reasonList = ref<string[]>([]);

const segment = ref(0);

const segmentItems = [
  { text: "休暇申請", value: "1" },
  { text: "振替申請", value: "0" }
] as const;

const isVacationKbnOther = computed(
  () => dat.value.vacationKbn === EnumWorkClassification.OTHER
);

const items = ref<Item[]>([]);

const substitute = ref<Item>();

const isOpen = ref(false);

const getLegalHoliday = async () => {
  const userId = dat.value.employeeId;
  const affiliationId = dat.value.affiliationId;
  const response = await $get<Item[]>(
    `${Paths.petition.search.legalHoliday}/${userId}/${affiliationId}`
  );
  if (response) items.value = response;
};

const formatDate = (item: Item) => {
  return DateTime.fromSQL(item.text)
    .setLocale("ja")
    .toFormat("yyyy年MM月dd日(EEE)");
};

const getCodeMaster = async (type: CodeKbnType) => {
  const code = await $get<PulldownResponse[]>(Paths.codeMasterPulldown);
  if (code) {
    reasonList.value = codeKbnFilter(code, type).map(e => e.name);
  }
};

watch(
  () => dat.value.vacationKbn,
  newValue => {
    if (
      newValue !== EnumWorkClassification.OTHER &&
      dat.value.resonKbn === ReasonKbn.special
    ) {
      dat.value.resonKbn = ReasonKbn.self;
    }
  }
);

watch(
  () => dat.value.resonKbn,
  async newValue => {
    switch (newValue) {
      case ReasonKbn.self:
        await getCodeMaster(CodeKbnList.SELRSN);
        break;
      case ReasonKbn.ceremonial:
        await getCodeMaster(CodeKbnList.CRERSN);
        break;
      case ReasonKbn.special:
        await getCodeMaster(CodeKbnList.SPRSN);
        break;
    }
    dat.value.resonText = reasonList.value.find(e => e === dat.value.resonText);
  },
  { immediate: true }
);

const initialize = async () => {
  dat.value = Object.assign({}, props.item);
  if (props.item.substituteShiftId) segment.value = 1;
  await getLegalHoliday();
};

watch(isOpen, async () => {
  if (isOpen.value) {
    await initialize();
  }
});

const isLoading = ref(false);

const onClose = () => {
  isOpen.value = false;
  emits("close");
};

const createRequest = () => {
  const formatDate = (date?: string) => {
    if (date)
      return DateTime.fromSQL(date)
        .setLocale("ja")
        .toISO();
  };
  const copy = Object.assign({}, dat.value);
  if (segment.value === 0) {
    copy.substituteShiftId = null;
    copy.substituteDate = null;
    return copy;
  } else {
    copy.vacationKbn = null;
    copy.resonKbn = null;
    copy.resonText = null;
    copy.substituteDate = formatDate(substitute.value?.text);
    copy.substituteShiftId = substitute.value?.value;
    return copy;
  }
};

const onSubmit = async () => {
  if (!vform.value?.validate()) {
    $alert("warning", "入力に誤りがあります。\n入力内容を確認してください。");
    return;
  }
  isLoading.value = true;

  const result = await $post(Paths.approval.vacation.update, createRequest());
  if (result) {
    $alert("success", "変更に成功しました。");
    onClose();
  } else {
    $alert("error", "変更に失敗しました。");
  }
  isLoading.value = false;
};

return { __sfc: true,vacationKbn, ReasonKbn, reasonKbn, props, emits, vform, dat, reasonList, segment, segmentItems, isVacationKbnOther, items, substitute, isOpen, getLegalHoliday, formatDate, getCodeMaster, initialize, isLoading, onClose, createRequest, onSubmit, InputRules }
}

})